import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Sparkles, Languages, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const { login, user, isAdmin } = useAuth();
  const { t, toggleLang, lang } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@sandbox.ai");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to={isAdmin ? "/" : "/unauthorized"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "admin" && u.status === "active" ? "/" : "/unauthorized", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || t("invalid_login"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between bg-primary p-12 text-primary-foreground relative overflow-hidden">
        <div className="absolute -top-24 -end-24 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute bottom-0 -start-24 h-80 w-80 rounded-full bg-accent/30 blur-3xl" />
        <div className="flex items-center gap-3 relative">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 backdrop-blur">
            <Sparkles className="h-6 w-6" />
          </div>
          <span className="text-lg font-bold">{t("app_name")}</span>
        </div>
        <div className="relative space-y-6">
          <h1 className="text-4xl font-bold leading-tight max-w-md">{t("app_sub")}</h1>
          <p className="text-base text-white/80 leading-relaxed max-w-md font-medium">"{t("principle")}"</p>
          <div className="flex gap-6 pt-4">
            {[["100", t("total_use_cases")], ["5", t("layer_readiness")], ["EN/AR", "RTL"]].map(([n, l]) => (
              <div key={l}><p className="text-3xl font-bold">{n}</p><p className="text-xs text-white/70">{l}</p></div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-white/60">Enterprise AI Value & Absorption Sandbox</div>
      </div>

      <div className="flex flex-col items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm space-y-8 fade-up">
          <div className="flex items-center justify-between">
            <div className="lg:hidden flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground"><Sparkles className="h-5 w-5" /></div>
            </div>
            <button onClick={toggleLang} data-testid="login-lang-toggle"
              className="ms-auto inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold hover:bg-secondary">
              <Languages className="h-4 w-4" /> {lang === "en" ? "العربية" : "English"}
            </button>
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-bold">{t("login_title")}</h2>
            <p className="text-sm text-muted-foreground">{t("login_desc")}</p>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t("email")}</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-11 rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t("password")}</Label>
              <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-11 rounded-xl" />
            </div>
            {error && <p data-testid="login-error" className="text-sm font-medium text-destructive">{error}</p>}
            <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full h-11 rounded-xl text-sm font-semibold">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("login")}
            </Button>
          </form>
          <p className="text-xs text-center text-muted-foreground rounded-lg bg-secondary py-2 px-3">{t("demo_hint")}</p>
        </div>
      </div>
    </div>
  );
}
